import type { DtcCodeEntity } from '@packages/domain/dtc-codes';
import type { TechlineAssetEntity } from '@packages/domain/techline/assets';
import type { TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { VinDetailEntity } from '@packages/domain/vin';
import type { FetchTitlesParams, WorksheetCaseGroupEntity } from '@packages/domain/worksheets/groups';

import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { noop } from '@packages/core/utils';
import { Step, Stepper } from '@web/components/stepper/stepper';
import { analyticsService } from '@web/services/singletons';
import { downloadFile } from '@web/utils';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { DraftStepperValidation } from '../../../hooks/use-draft-stepper-validation';
import type { WorksheetGroup, WorksheetOpenMode } from '../../../worksheet-group-accordion';
import type { WorksheetSubmissionGroup } from './techline-draft-stepper.types';

import { ConcernDetailsStepContent } from './steps/concern-details-step-content/concern-details-step-content';
import { TechnicalDescriptionStepContent } from './steps/technical-description-step-content/technical-description-step-content';
import { WorksheetsStepContent } from './steps/worksheets-step-content/worksheets-step-content';

/** Props for the TechlineDraftStepper component. */
export type TechlineDraftStepperProps = {
    /** Case-level file attachments. */
    assetData: TechlineAssetEntity[];

    /** The case details for the current draft case. */
    caseDetails: TechlineCaseEntity;

    /** The case number being edited. */
    caseNumber: string;

    /** Validation state and touched field tracking for the draft stepper. */
    draftStepperValidation: DraftStepperValidation;

    /** Initial step index to display (from URL search params). */
    initialStep?: number | null;

    /** Whether worksheet groups are still being fetched from the API. */
    isLoadingGroups?: boolean;

    /** Callback when case details change (e.g., form fields). */
    onCaseDetailsChange: (data: Partial<TechlineCaseEntity>) => void;

    /** Callback to delete an asset by ID. */
    onDeleteAsset: (assetId: string) => Promise<void>;

    /** Callback to delete a draft worksheet submission. */
    onDeleteDraftWorksheetSubmission: (submissionId: string) => Promise<boolean>;

    /** Callback to set an error message banner in the parent view. */
    onError?: (message: string) => void;

    /** Callback to fetch worksheet titles for a group. */
    onFetchWorksheetTitles: (params: FetchTitlesParams) => Promise<WorksheetCaseGroupEntity[] | undefined>;

    /** Callback to open a worksheet (new or existing). */
    onOpenWorksheet?: (worksheetId: string, submissionId: string | undefined, mode: WorksheetOpenMode) => void;

    /** Callback to refresh the asset list. */
    onRefreshAssets: () => Promise<void>;

    /** Callback to submit the case for review. */
    onSubmit?: () => void;

    /** Callback to set a success message banner in the parent view. */
    onSuccess?: (message: string) => void;

    /** Callback to update an existing asset. */
    onUpdateAsset: (assetId: string, update: AssetUpdate) => Promise<void>;

    /** Callback to upload a new asset file. */
    onUploadAsset: (file: File) => Promise<void>;

    /** Callback when the form's overall validation state changes. */
    onValidationChange?: (isValid: boolean) => void;

    /** Vehicle VIN data for the case. */
    vehicleVinData: VinDetailEntity | undefined;

    /** All available worksheet groups with titles for the worksheets step. */
    worksheetGroupData?: WorksheetGroup[];

    /** Error message from loading available worksheet groups. */
    worksheetGroupError?: string;

    /** Worksheet submission groups from the case details. */
    worksheetSubmissionGroups?: WorksheetSubmissionGroup[];

    /** Validation results for draft worksheets. Keyed by worksheetId; true if valid. */
    worksheetValidationResults?: Record<string, boolean>;

    /** DTC code data for the case. */
    dtcCodeData: DtcCodeEntity[];

    /** Whether DTC codes are currently being loaded. */
    isDtcLoading: { [dtcCode: string]: boolean };

    /** Whether the primary DTC code is currently being updated. */
    isPrimaryDtcUpdating: boolean;

    /** Error message related to DTC code operations. */
    dtcErrorMessage: string;

    /** Callback to add a DTC code. */
    onAddDtc: (dtc: DtcCodeEntity[]) => Promise<void>;

    /** Callback to delete a DTC code. */
    onDeleteDtc: (dtc: DtcCodeEntity) => Promise<void>;

    /** Callback to change the primary DTC code. */
    onPrimaryChange: (dtc: DtcCodeEntity) => Promise<void>;

    /** Callback to toggle freeze frame data for a DTC code. */
    onToggleFreezeFrame: (dtc: DtcCodeEntity) => Promise<void>;
};

/** Update payload for modifying an existing asset (rename, note change, or blob replacement). */
type AssetUpdate = {
    /** A replacement file blob, if re-uploading. */
    blob?: Blob;

    /** A new display name for the asset. */
    name?: string;

    /** A new note/description for the asset. */
    note?: string;
};

const START_STEP_INDEX = 0;

/**
 * Multi-step stepper for editing a draft Techline case. Renders three steps
 * (Concern Details, Technical Description, Worksheets) and delegates all
 * validation state to the {@link DraftStepperValidation} prop (managed by
 * useDraftStepperValidation in useTechlineCase).
 *
 * @param props - the stepper configuration and callback props
 * @returns the multi-step draft case stepper
 */
export function TechlineDraftStepper({
    assetData,
    caseDetails,
    caseNumber,
    dtcCodeData,
    dtcErrorMessage,
    draftStepperValidation,
    initialStep,
    isDtcLoading,
    isLoadingGroups = false,
    isPrimaryDtcUpdating,
    onAddDtc,
    onCaseDetailsChange,
    onDeleteAsset,
    onDeleteDraftWorksheetSubmission,
    onDeleteDtc,
    onError,
    onFetchWorksheetTitles: _onFetchWorksheetTitles,
    onOpenWorksheet,
    onPrimaryChange,
    onRefreshAssets,
    onSubmit,
    onSuccess,
    onToggleFreezeFrame,
    onUpdateAsset,
    onUploadAsset,
    onValidationChange,
    vehicleVinData: _vehicleVinData,
    worksheetGroupData = [],
    worksheetGroupError = '',
    worksheetSubmissionGroups,
    worksheetValidationResults = {},
}: TechlineDraftStepperProps) {
    const [, setSearchParams] = useSearchParams();

    // State
    const [currentStepIndex, setCurrentStepIndex] = useState(initialStep != null ? initialStep : START_STEP_INDEX);

    // Destructure validation from the hook
    const { errors, stepValidation, touched, handleTouchedChange, handleBeforeStepChange, initializeForStep } =
        draftStepperValidation;

    // Forwarded callback aliases
    const handleAddDtc = onAddDtc;
    const handleCaseDetailsChange = onCaseDetailsChange ?? noop;
    const handleDeleteAsset = onDeleteAsset;
    const handleDeleteDraftWorksheetSubmission = onDeleteDraftWorksheetSubmission;
    const handleDeleteDtc = onDeleteDtc;
    const handleError = onError ?? noop;
    const handleOpenWorksheet = onOpenWorksheet ?? noop;
    const handlePrimaryChange = onPrimaryChange;
    const handleRefreshAssets = onRefreshAssets;
    const handleSubmit = onSubmit ?? noop;
    const handleSuccess = onSuccess ?? noop;
    const handleToggleFreezeFrame = onToggleFreezeFrame;
    const handleUpdateAsset = onUpdateAsset;
    const handleUploadAsset = onUploadAsset;

    // /**
    //  * Adapter that wraps onFetchWorksheetTitles to match the simplified
    //  * signature expected by WorksheetsStepContent. Currently a placeholder;
    //  * full group-based title loading will be wired in a subsequent pass.
    //  *
    //  * @param _groupId - the worksheet group to load titles for (not yet used)
    //  */
    const handleLoadWorksheetTitles = useCallback((_groupId: string) => {
        // Worksheet title loading will be fully wired in a follow-up task.
        // The data flow currently goes through worksheetTitleData prop.
    }, []);

    /**
     * Handles a touched event from the Concern Details step by delegating
     * to handleTouchedChange with the correct step key.
     *
     * @param field - the field name that was touched
     */
    const handleConcernDetailsTouched = useCallback(
        (field: string) => {
            handleTouchedChange('concernDetailsStep', field);
        },
        [handleTouchedChange]
    );

    /**
     * Handles a touched event from the Technical Description step by delegating
     * to handleTouchedChange with the correct step key.
     *
     * @param field - the field name that was touched
     */
    const handleTechnicalDescriptionTouched = useCallback(
        (field: string) => {
            handleTouchedChange('technicalDescriptionStep', field);
        },
        [handleTouchedChange]
    );

    /**
     * Handles a touched event from the Worksheets step by delegating
     * to handleTouchedChange with the correct step key.
     *
     * @param field - the field name that was touched
     */
    const handleWorksheetsTouched = useCallback(
        (field: string) => {
            handleTouchedChange('worksheetsStep', field);
        },
        [handleTouchedChange]
    );

    /**
     * Handles a step change event by updating the current step index state
     * and syncing it to the URL search params.
     *
     * @param stepIndex - the new step index to switch to
     */
    const handleStepChange = useCallback(
        (stepIndex: number) => {
            if (!stepValidation.isFormValid) {
                analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_VALIDATION_FAILED, {
                    stepName: stepValidation.isConcernDetailsInvalid
                        ? 'CONCERN'
                        : stepValidation.isTechnicalDescriptionInvalid
                          ? 'TECHNICAL_DESCRIPTION'
                          : 'WORKSHEETS',
                    ...stepValidation,
                });
            } else {
                analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_REQUIRED_FIELDS_COMPLETED, {
                    stepName: stepIndex === 0 ? 'CONCERN' : stepIndex === 1 ? 'TECHNICAL_DESCRIPTION' : 'WORKSHEETS',
                    ...stepValidation,
                });
            }
            setCurrentStepIndex(stepIndex);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('step', String(stepIndex));
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams, stepValidation]
    );

    // Effects

    /** Notifies the parent whenever the form's overall validation state changes. */
    useEffect(() => {
        onValidationChange?.(stepValidation.isFormValid);
    }, [stepValidation.isFormValid, onValidationChange]);

    /** On mount, if landing on a step beyond 0 (e.g. page refresh), validate that step and all previous. */
    useEffect(() => {
        if (initialStep != null && initialStep > 0) {
            initializeForStep(initialStep);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Stepper
                currentStepIndex={currentStepIndex}
                disableSubmit={!stepValidation.isFormValid}
                onBeforeStepChange={handleBeforeStepChange}
                onStepChange={handleStepChange}
                onSubmit={handleSubmit}
                submitTooltipText={
                    !stepValidation.isFormValid ? 'Please complete all required steps before submitting.' : undefined
                }
            >
                <Step
                    id="concernDetails"
                    isComplete={stepValidation.isConcernDetailsComplete}
                    isInvalid={stepValidation.isConcernDetailsInvalid}
                    label="techline:concernDetails.stepLabel"
                >
                    <ConcernDetailsStepContent
                        caseDetails={caseDetails}
                        errors={errors.concernDetailsStep}
                        onCaseDetailsChange={handleCaseDetailsChange}
                        onTouched={handleConcernDetailsTouched}
                        touched={touched.concernDetailsStep}
                    />
                </Step>

                <Step
                    id="techDescription"
                    isComplete={stepValidation.isTechnicalDescriptionComplete}
                    isInvalid={stepValidation.isTechnicalDescriptionInvalid}
                    label="techline:techDescription.stepLabel"
                >
                    <TechnicalDescriptionStepContent
                        assetData={assetData}
                        caseDetails={caseDetails}
                        dtcCodeData={dtcCodeData}
                        dtcErrorMessage={dtcErrorMessage}
                        errors={errors.technicalDescriptionStep}
                        isDtcLoading={isDtcLoading}
                        isPrimaryDtcUpdating={isPrimaryDtcUpdating}
                        isUploadingAssets={false}
                        onAddDtc={handleAddDtc}
                        onCaseDetailsChange={handleCaseDetailsChange}
                        onDeleteAsset={handleDeleteAsset}
                        onDeleteDtc={handleDeleteDtc}
                        onDownloadAsset={downloadFile}
                        onError={handleError}
                        onPrimaryChange={handlePrimaryChange}
                        onRefreshAssets={handleRefreshAssets}
                        onSuccess={handleSuccess}
                        onToggleFreezeFrame={handleToggleFreezeFrame}
                        onTouched={handleTechnicalDescriptionTouched}
                        onUpdateAsset={handleUpdateAsset}
                        onUploadAsset={handleUploadAsset}
                        touched={touched.technicalDescriptionStep}
                        uploadingFiles={[]}
                    />
                </Step>
                <Step
                    id="caseType"
                    isComplete={stepValidation.isWorksheetsComplete}
                    isInvalid={stepValidation.isWorksheetsInvalid}
                    label="techline:worksheets.stepLabel"
                >
                    <WorksheetsStepContent
                        caseDetails={caseDetails}
                        caseNumber={caseNumber}
                        isInitializingWorksheets={false}
                        isLoadingGroups={isLoadingGroups}
                        onCaseDetailsChange={handleCaseDetailsChange}
                        onDeleteWorksheet={handleDeleteDraftWorksheetSubmission}
                        onLoadWorksheetTitles={handleLoadWorksheetTitles}
                        onOpenWorksheet={handleOpenWorksheet}
                        onTouched={handleWorksheetsTouched}
                        worksheetGroupData={worksheetGroupData}
                        worksheetGroupError={worksheetGroupError}
                        worksheetSubmissionGroups={worksheetSubmissionGroups}
                        worksheetValidationResults={worksheetValidationResults}
                    />
                </Step>
            </Stepper>
        </>
    );
}
